import { startOfISOWeek } from "date-fns";

import store from "../../";
import StoreMatchController from "./storeMatchController";

import initCalendarData from "./initCalendarData";

import { MAX_SESSION_LENGTH_MS, COVERED_BY_SLOT_COUNT, SLOT_INTERVAL_MS } from "../../../constants_calendar";
const ONE_WEEK = 604800000;

/**
 * Updates availabilities by checking
 */
function updateByArray(array) {
  // console.log("UPDATING DATETIME ARRAY: dates: ", array);
  const unavailableDates = new Set();
  for (let i = 0; i < array.length; i++) {
    for (let j = -COVERED_BY_SLOT_COUNT; j <= COVERED_BY_SLOT_COUNT; j++) {
      unavailableDates.add(array[i] + j * SLOT_INTERVAL_MS);
    }
  }
  // console.log("@updateByArray array:", array);
  // console.log("@updateByArray unavailableDates: ", unavailableDates);
  store.dispatch("calendar/setSlotArrayFieldStatus", {
    dateTimeMSArray: Array.from(unavailableDates),
    field: "isAvailableForBooking",
    value: false,
  });
}

/**
 * Removes selections that would overlap with an upcoming match
 * @param {Array} matchDateTimes *sorted* array of upcoming match datetimes
 * @param {Array} selections *sorted* array of selected datetimes
 */
function removeOverlappingSelections(matchDateTimes, selections) {
  let matchIdx = 0;
  const selectionsToCancel = [];
  for (let i = 0; i < selections.length; i++) {
    let currentMatchDate = matchIdx < matchDateTimes.length ? matchDateTimes[matchIdx] : null;
    if (currentMatchDate) {
      /* make sure that currentMatchDate is larger than current selection datetime */
      while (selections[i] > currentMatchDate + 3 * SLOT_INTERVAL_MS) {
        // console.log("in loop selection: ", new Date(selections[i]));
        // console.log("in loop match:", new Date(currentMatchDate));
        matchIdx++;
        if (matchIdx >= matchDateTimes.length) {
          console.log("broke: ", i, matchIdx);
          break;
        }
        // console.log("still in loop: ", matchIdx);
        currentMatchDate = matchDateTimes[matchIdx];
      }

      // console.log("after loop selection: ", new Date(selections[i]));
      // console.log("after loop date: ", new Date(currentMatchDate));
      if (currentMatchDate - 3 * SLOT_INTERVAL_MS <= selections[i] && selections[i] <= currentMatchDate + 3 * SLOT_INTERVAL_MS) {
        // console.log("found selection to cancel: ", selections[i], currentMatchDate);
        selectionsToCancel.push(selections[i]);
      }
    }
  }
  for (let i = 0; i < selectionsToCancel.length; i++) {
    /* just remove, do not update availabilities */
    store.dispatch("selections/cancelSlot", selectionsToCancel[i]);
  }
}

async function updatePostLoad(viewStartMS, viewEndMS) {
  /* reset all availabilities */
  // console.log("@updatePostLoad", viewStartMS, viewEndMS);
  const allDates = Array.from(new Array((viewEndMS - viewStartMS) / SLOT_INTERVAL_MS), (_, i) => viewStartMS + i * SLOT_INTERVAL_MS);
  store.dispatch("calendar/setSlotArrayFieldStatus", {
    dateTimeMSArray: allDates,
    field: "isAvailableForBooking",
    value: true,
  });
  const matchDateTimes = await store.dispatch("upcomingMatches/getMatchesInRange", { viewStartMS, viewEndMS });
  const selections = store.state.selections.selectedDateTimes;
  removeOverlappingSelections(matchDateTimes, selections);
  updateByArray(matchDateTimes);
  updateByArray(selections);
}

/**
 * @param {String} userID the ID of the current user
 * @param {Number} dataStart the datetime from which to request from DB
 * @param {Number} dataEnd the datetime until which to request from DB
 */
async function setDataForView(userID, dataStart, dataEnd) {
  const promiseArray = [];
  promiseArray.push(store.dispatch("calendar/addDateTimeRange", { rangeStart: dataStart, rangeEnd: dataEnd }));
  promiseArray.push(store.dispatch("matchPool/addDateTimeRange", { rangeStart: dataStart, rangeEnd: dataEnd }));
  await Promise.all(promiseArray);
  // console.log("@setDataForView data initialized: ");
  // console.log("@setDataForView data range: ", dataStart,"-",dataEnd, ": ", new Date(dataStart),"-",new Date(dataEnd));
  try {
    await StoreMatchController.loadDataFromDatabase(userID, dataStart, dataEnd);
  } catch (error) {
    console.error("@setDataForView error: ", error);
  }
  return true;
}

/**
 * Called on initial load. NB! Assumes that the calendar is in week view
 * @param {String} userID the current user's ID
 */
async function initializeCalendar(userID) {
  const viewStart = startOfISOWeek(Date.now()).valueOf();
  const viewEnd = startOfISOWeek(Date.now()).valueOf() + ONE_WEEK;
  const dataStart = viewStart - MAX_SESSION_LENGTH_MS;
  const dataEnd = viewEnd + MAX_SESSION_LENGTH_MS;
  await store.dispatch("calendar/setView", {
    viewStart: viewStart,
    viewEnd: viewEnd,
  });
  await initCalendarData(userID, dataStart, dataEnd);
  updatePostLoad(viewStart, viewEnd);

  return true;
}

/**
 * @param {String} userID current user ID
 * @param {Number} newViewStart the start of new view in MS
 * @param {Number} newViewEnd the end of new view in MS
 */
async function switchView(userID, newViewStart, newViewEnd) {
  const dataStart = getDataStart(newViewStart);
  const dataEnd = getDataEnd(newViewEnd);
  // console.log("@switchView ", dataStart, dataEnd);
  if (dataStart < dataEnd) {
    /* no data for given timeframe, need to request data and initialize new range */
    await setDataForView(userID, dataStart, dataEnd);
    /* then update availabilities of slots in the view */
    updatePostLoad(newViewStart, newViewEnd);
  }
  store.dispatch("calendar/setView", { viewStart: newViewStart, viewEnd: newViewEnd });
}

/**
 * Used during manual / iterative refresh.
 * @param {String} userID current user ID
 * @param {Number} viewStart the view start which needs to be refreshed
 * @param {Number} viewEnd the view end
 */
async function refreshCurrentView(userID, viewStart, viewEnd) {
  const dataStart = viewStart - MAX_SESSION_LENGTH_MS;
  const dataEnd = viewEnd + MAX_SESSION_LENGTH_MS;
  // console.log("@refresh start: ", new Date(dataStart));
  // console.log("@refresh end: ", new Date(dataEnd));
  // TODO: do not request past matches
  /* await setDataForView(userID, dataStart, dataEnd); */
  await initCalendarData(userID, dataStart, dataEnd);
  updatePostLoad(viewStart, viewEnd);
}

const ViewController = {
  initializeCalendar,
  switchView,
  refreshCurrentView,
};

export default ViewController;

/* ================ HELPERS ================ */

function getDataStart(newViewStart) {
  const currentDataStart = store.getters["calendar/getDataStart"];
  if (newViewStart - MAX_SESSION_LENGTH_MS < currentDataStart) {
    return newViewStart - MAX_SESSION_LENGTH_MS;
  }
  return store.getters["calendar/getDataEnd"];
}
function getDataEnd(newViewEnd) {
  const currentDataEnd = store.getters["calendar/getDataEnd"];
  if (newViewEnd + MAX_SESSION_LENGTH_MS > currentDataEnd) {
    return newViewEnd + MAX_SESSION_LENGTH_MS;
  }
  return store.getters["calendar/getDataStart"];
}
